import { useState, useEffect } from 'react';
import { MapPin, Phone, Star, Plus, Filter, Search, ChevronDown, Stethoscope, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { isChronicEnabled } from '../config/appMode';
import {
  MEDICAL_DOCTOR_TYPES,
  MEDICAL_SPECIALIST_TYPES,
  FITNESS_SPECIALIST_TYPES,
  ALL_SPECIALTY_LABELS,
  getCategoryForType as getCategoryFromConfig,
  type SpecialtyCategory,
} from '../config/specialtyTypes';


// Fix Leaflet default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Specialist {
  id: string;
  name: string;
  title?: string;
  specialtyType: string;
  phone: string;
  email?: string;
  website?: string;
  avatarUrl?: string;
  address: string;
  city: string;
  county: string;
  latitude: number;
  longitude: number;
  description?: string;
  averageRating: number;
  totalReviews: number;
  acceptsNewPatients: boolean;
}

// În FII FIT afișăm DOAR specializările fitness. În varianta legacy
// (CHRONIC) afișăm doctorii și specialiștii medicali.
const CHRONIC_MODE = isChronicEnabled();
const doctorTypes: Record<string, string> = CHRONIC_MODE ? MEDICAL_DOCTOR_TYPES : {};
const specialistTypes: Record<string, string> = CHRONIC_MODE
  ? MEDICAL_SPECIALIST_TYPES
  : FITNESS_SPECIALIST_TYPES;

// Label lookup folosește harta completă (orice tip primit din BD are label).
const specialtyLabels: Record<string, string> = ALL_SPECIALTY_LABELS;

// Categoria principală — în FII FIT "FITNESS" e singura disponibilă.
const getCategoryForType = (type: string): SpecialtyCategory | null =>
  getCategoryFromConfig(type);


const romanianCounties = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brașov',
  'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj', 'Constanța',
  'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara',
  'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt',
  'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea',
  'Vaslui', 'Vâlcea', 'Vrancea'
];

export default function SpecialistsPage() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  // Filters
  // În FII FIT (FITNESS) avem o singură categorie reală: "SPECIALIST" (fitness),
  // deci pre-selectăm direct categoria și ascundem tab-urile "Toți / Doctori / Specialiști".
  // În varianta modul CHRONIC păstrăm comportamentul original (gol = "Toți").
  const [mainCategory, setMainCategory] = useState<'' | 'DOCTOR' | 'SPECIALIST'>(
    CHRONIC_MODE ? '' : 'SPECIALIST'
  );

  const [selectedType, setSelectedType] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Reset when filters change
  useEffect(() => {
    setPage(1);
    setSpecialists([]);
    setHasMore(true);
    fetchSpecialists(true);
  }, [selectedType, selectedCounty, mainCategory]);

  // Fetch when page changes (load more)
  useEffect(() => {
    if (page > 1) {
      fetchSpecialists(false);
    }
  }, [page]);

  // When main category changes, reset the sub-type
  const handleMainCategoryChange = (cat: '' | 'DOCTOR' | 'SPECIALIST') => {
    setMainCategory(cat);
    setSelectedType('');
  };


  const fetchSpecialists = async (reset = false) => {
    const isInitialLoad = reset || page === 1;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('page', (reset ? 1 : page).toString());
      if (selectedType) params.append('specialtyType', selectedType);
      if (selectedCounty) params.append('county', selectedCounty);

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/specialists?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newSpecialists = response.data.specialists || [];
      const total = response.data.pagination?.total || 0;

      if (reset) {
        setSpecialists(newSpecialists);
      } else {
        setSpecialists(prev => [...prev, ...newSpecialists]);
      }

      setTotalCount(total);
      setHasMore(newSpecialists.length === limit);
    } catch (error) {
      console.error('Error fetching specialists:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  // Client-side filtering: search term + main category (when no sub-type selected)
  const filteredSpecialists = specialists.filter(s => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.city.toLowerCase().includes(searchTerm.toLowerCase());

    // If a sub-type is selected, the API already filtered; just apply search
    if (selectedType) return matchesSearch;

    // If a main category is selected but no sub-type, filter client-side by category
    if (mainCategory) {
      // in FITNESS mode the single 'SPECIALIST' tab lists the fitness specialists
      const wantedCategory = !CHRONIC_MODE && mainCategory === 'SPECIALIST' ? 'FITNESS' : mainCategory;
      return matchesSearch && getCategoryForType(s.specialtyType) === wantedCategory;
    }

    return matchesSearch;
  });

  // Which sub-types to show in the second dropdown
  const subTypeOptions =
    mainCategory === 'DOCTOR'
      ? doctorTypes
      : mainCategory === 'SPECIALIST'
      ? specialistTypes
      : {};

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Specialiști Fitness"
        showBack={true}
        backTo="/dashboard"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Filters ─────────────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">

          {/* Row 1 – Main category tabs (vizibile doar în CHRONIC; în
              FII FIT avem doar specialiști fitness, deci tab-ul e inutil). */}
          {CHRONIC_MODE && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="inline w-4 h-4 mr-1" />
                Categorie
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleMainCategoryChange('')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                    mainCategory === ''
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                  }`}
                >
                  Toți
                </button>

                <button
                  onClick={() => handleMainCategoryChange('DOCTOR')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                    mainCategory === 'DOCTOR'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                  }`}
                >
                  <Stethoscope className="w-4 h-4" />
                  Doctori
                </button>

                <button
                  onClick={() => handleMainCategoryChange('SPECIALIST')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                    mainCategory === 'SPECIALIST'
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:text-purple-600'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Specialiști
                </button>
              </div>
            </div>
          )}


          {/* Row 2 – Sub-type + County + Search */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Sub-type dropdown (only when a main category is selected) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specialitate
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                disabled={!mainCategory}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {mainCategory ? 'Toate specialitățile' : 'Selectează categoria mai întâi'}
                </option>
                {Object.entries(subTypeOptions).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* County */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline w-4 h-4 mr-1" />
                Județ
              </label>
              <select
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toate județele</option>
                {romanianCounties.map(county => (
                  <option key={county} value={county}>{county}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="inline w-4 h-4 mr-1" />
                Caută
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nume sau oraș..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* ── Map ─────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm p-2 mb-6" style={{ height: '500px' }}>
          <MapContainer
            center={[45.9432, 24.9668]}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
            className="rounded-lg"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {filteredSpecialists.map(specialist => (
              <Marker
                key={specialist.id}
                position={[specialist.latitude, specialist.longitude]}
              >
                <Popup>
                  <div className="p-2">
                    <h4 className="font-semibold">{specialist.name}</h4>
                    <p className="text-sm text-gray-600">{specialtyLabels[specialist.specialtyType] || specialist.specialtyType}</p>
                    <p className="text-sm">{specialist.city}</p>
                    <button
                      onClick={() => navigate(`/specialists/${specialist.id}`)}
                      className="mt-2 text-blue-600 text-sm font-medium"
                    >
                      Vezi detalii →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* ── Counter ─────────────────────────────────────────────────── */}
        {!loading && filteredSpecialists.length > 0 && (
          <div className="bg-blue-50 rounded-lg px-6 py-3 mb-6">
            <p className="text-sm text-gray-700">
              📊 Afișare: <span className="font-semibold">{filteredSpecialists.length}</span> din <span className="font-semibold">{totalCount}</span> specialiști
              {(selectedType || selectedCounty || mainCategory) && ' (filtrat)'}
            </p>
          </div>
        )}

        {/* ── List ────────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-500">Se încarcă specialiștii...</p>
            </div>
          ) : filteredSpecialists.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <MapPin className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-500">Niciun specialist găsit</p>
            </div>
          ) : (
            <>
              {filteredSpecialists.map(specialist => {
                const cat = getCategoryForType(specialist.specialtyType);
                return (
                  <div
                    key={specialist.id}
                    onClick={() => navigate(`/specialists/${specialist.id}`)}
                    className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex gap-4 mb-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {specialist.avatarUrl ? (
                          <img
                            src={`${import.meta.env.VITE_API_URL}${specialist.avatarUrl}`}
                            alt={specialist.name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl">
                            {specialist.name.charAt(0)}
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {specialist.title && `${specialist.title} `}{specialist.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span
                                className={`text-sm font-medium ${
                                  cat === 'DOCTOR'
                                    ? 'text-blue-600'
                                    : cat === 'FITNESS'
                                    ? 'text-emerald-600'
                                    : 'text-purple-600'
                                }`}
                              >
                                {specialtyLabels[specialist.specialtyType] || specialist.specialtyType}
                              </span>
                              {cat && (
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full ${
                                    cat === 'DOCTOR'
                                      ? 'bg-blue-100 text-blue-700'
                                      : cat === 'FITNESS'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-purple-100 text-purple-700'
                                  }`}
                                >
                                  {cat === 'DOCTOR'
                                    ? 'Doctor'
                                    : cat === 'FITNESS'
                                    ? 'Fitness'
                                    : 'Specialist'}
                                </span>
                              )}
                            </div>

                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-5 h-5 text-yellow-500 fill-current" />
                            <span className="font-semibold">{specialist.averageRating.toFixed(1)}</span>
                            <span className="text-sm text-gray-500">({specialist.totalReviews})</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 ml-20">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} />
                        <span>{specialist.address}, {specialist.city}, {specialist.county}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone size={16} />
                        <span>{specialist.phone}</span>
                      </div>
                      {specialist.acceptsNewPatients && (
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          ✓ Acceptă clienți noi
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Load More */}
              {hasMore && !loading && (
                <div className="flex justify-center pt-6">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-md"
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Se încarcă...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-5 h-5" />
                        Încarcă următorii {limit} specialiști
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* FAB – Add Specialist (only for SPECIALIST or ADMIN) */}
        {(user?.userType === 'SPECIALIST' || user?.role === 'ADMIN') && (
          <button
            onClick={() => navigate('/specialist/profile/edit')}
            className="fixed bottom-8 right-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
            title="Adaugă/Editează profil"
          >
            <Plus size={24} />
          </button>
        )}
      </main>
    </div>
  );
}
